import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  MapPin,
  Building2,
  Languages,
  Wallet,
  Calendar,
  CalendarCheck,
} from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Job } from '../../types/job';
import { JobStatus } from '../../types/common';
import { formatDateLocalized } from '../../utils/dateUtils';
import {
  getJobExpirationInfo,
  getJobListingTypeLabel,
  getJobSelectionInfo,
} from '../../utils/jobUtils';
import { getFreelancerSubtitle } from '../../utils/freelancerUtils';
import JobListingType from './JobListingType';
import JobPublishedDate from './JobPublishedDate';

const STATUS_PILL_STYLES: Record<JobStatus, string> = {
  open: 'bg-primary/10 text-primary-over',
  in_progress: 'bg-primary/10 text-primary-over',
  paused: 'bg-amber-50 text-amber-700',
  completed: 'bg-secondary/5 text-secondary',
  cancelled: 'bg-red-50 text-red-700',
  expired: 'bg-red-50 text-red-700',
  reviewing_quotes: 'bg-amber-50 text-amber-700',
};

const STATUS_DOT_STYLES: Record<JobStatus, string> = {
  open: 'bg-primary',
  in_progress: 'bg-primary',
  paused: 'bg-amber-500',
  completed: 'bg-secondary-light',
  cancelled: 'bg-red-600',
  expired: 'bg-red-600',
  reviewing_quotes: 'bg-amber-500',
};

const DetailField: React.FC<{
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: React.ReactNode;
}> = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-secondary/10 bg-light p-[18px] transition-colors hover:border-primary/25">
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10">
        <Icon size={17} className="text-primary-over" aria-hidden />
      </span>
      <span className="text-[12.5px] font-medium text-[#304659]">{label}</span>
    </div>
    <div className="text-base font-bold leading-tight text-secondary">{value}</div>
  </div>
);

const JobDetailsCard: React.FC<{
  job: Job;
  onEdit?: (jobId: string) => void;
  onChangePriority?: (jobId: string) => void;
  onPublish?: (jobId: string) => void;
}> = ({ job }) => {
  const { t, i18n } = useTranslation(['jobs', 'common']);

  const jobListingTypeLabel = getJobListingTypeLabel(job, t);
  const expirationInfo = getJobExpirationInfo(job, t, i18n.language);
  const selectionInfo = getJobSelectionInfo(job, t, i18n.language);
  const statusLabel = t(`job_statuses.${job.status}`, { ns: 'common' });

  const header = (
    <div className="flex flex-col gap-6 border-b border-secondary/10 px-5 pb-7 pt-6 sm:px-8 md:flex-row md:items-start md:justify-between md:px-11 md:pt-10">
      <div className="min-w-0 flex-1">
        <div
          className={twMerge(
            'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide',
            STATUS_PILL_STYLES[job.status]
          )}
        >
          <span className={twMerge('h-1.5 w-1.5 rounded-full', STATUS_DOT_STYLES[job.status])} />
          {statusLabel}
        </div>
        <h2 className="mt-4 pr-8 text-2xl font-bold leading-tight text-secondary sm:text-[1.75rem] md:pr-0">
          {job.title}
        </h2>
      </div>
      <div className="flex items-end gap-6 md:items-start md:gap-4">
        <JobPublishedDate
          job={job}
          t={t}
          language={i18n.language}
          className="hidden text-right md:flex"
        />

        {jobListingTypeLabel && <JobListingType job={job} t={t} />}
      </div>
    </div>
  );

  return (
    <>
      {header}

      <div className="px-5 pb-2 pt-6 sm:px-8 md:px-11 md:pt-8">
        <h3 className="mb-6 text-lg font-bold text-secondary">
          {t('project_info', { ns: 'jobs', defaultValue: 'Informations du projet' })}
        </h3>

        <div className="mb-7 flex flex-col gap-5 rounded-2xl bg-gradient-to-br from-primary to-primary-over p-5 sm:flex-row sm:items-center sm:p-6">
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10">
              <Wallet size={22} className="text-white/90" aria-hidden />
            </span>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-white/90">
                {t('budget', { ns: 'jobs' })}
              </div>
              <div className="mt-0.5 text-lg font-bold text-white sm:text-xl">
                {t(`budget_ranges.${job.budget_range.code}`, { ns: 'common' })}
              </div>
            </div>
          </div>

          {expirationInfo && (
            <div className="border-t border-white/15 pt-4 sm:ml-auto sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-right">
              <div className="text-xs font-semibold text-white/90">{expirationInfo.label}</div>
              <div
                className={twMerge(
                  'mt-0.5 text-sm font-semibold text-white',
                  expirationInfo.expired && 'text-red-50'
                )}
              >
                {expirationInfo.value}
              </div>
            </div>
          )}

          {selectionInfo && (
            <div className="border-t border-white/15 pt-4 sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-right">
              <div className="text-xs font-semibold text-white/90">{selectionInfo.label}</div>
              <div className="mt-0.5 text-sm font-semibold text-white">{selectionInfo.value}</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 md:grid-cols-3">
          <DetailField
            icon={Briefcase}
            label={t('service_type', { ns: 'jobs' })}
            value={getFreelancerSubtitle(
              { service_category_slugs: job.required_services.map(s => s.slug) },
              t
            )}
          />
          <DetailField
            icon={MapPin}
            label={t('form.location', { ns: 'jobs' })}
            value={job.city?.name}
          />
          <DetailField
            icon={Building2}
            label={t('form.onsite', { ns: 'jobs' })}
            value={
              job.on_site
                ? t('form_fields.yes', { ns: 'common' })
                : t('form_fields.no', { ns: 'common' })
            }
          />
          <DetailField
            icon={Languages}
            label={t('form.languages', { ns: 'jobs' })}
            value={job.languages?.map(l => t(`language.${l}`, { ns: 'common' })).join(', ')}
          />
          <DetailField
            icon={Calendar}
            label={t('form.start_date', { ns: 'jobs' })}
            value={
              job.start_date &&
              formatDateLocalized(job.start_date, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            }
          />
          {job.end_date && (
            <DetailField
              icon={CalendarCheck}
              label={t('form.end_date', { ns: 'jobs' })}
              value={formatDateLocalized(job.end_date, i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            />
          )}
        </div>

        <div className="mt-7 rounded-2xl border border-secondary/10 bg-light px-6 py-[22px]">
          <div className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-[#304659]">
            {t('description_label', { ns: 'jobs' })}
          </div>
          <p className="m-0 text-base leading-relaxed text-secondary/80">{job.description}</p>
        </div>
      </div>
    </>
  );
};

export default JobDetailsCard;
